import type { AxiosError, AxiosRequestConfig } from 'axios';

import Router from 'next/router';
import axios from 'axios';
import ExpiryMap from 'expiry-map';
import { isEmpty } from 'lodash';
import memoize from 'p-memoize';
import { v4 as uuidv4 } from 'uuid';

import { browserEnvs } from '@paid-ui/env';
import { contractManager } from '@paid-ui/models/contract';
import { projectManager } from '@paid-ui/models/project';
import { userManager } from '@paid-ui/models/user';
import { asciiPattern } from '@paid-ui/regexps/ascii';
import { ASCII_HYPHEN, NON_ASCII_HYPHEN_REGEX } from '@paid-ui/regexps/hyphen';
import { NULL_BYTE_REGEX } from '@paid-ui/regexps/null-byte';

import { reportAppError } from './error-report';

const { apiBaseUrl } = browserEnvs;

interface RefreshTokenResponse {
  data: {
    access_token: string;
  };
}

/**
 * A failed response, with the backend's own error code kept alongside its message.
 *
 * The message is written for whoever is reading the logs — it changes without notice and
 * says whatever the failing service felt like saying — so a screen that needs to tell one
 * failure from another has to match on the code. Dropping it left the caller with a string
 * to pattern-match, which is how "wrong code" and "expired code" end up sharing a message.
 *
 * Still an `Error`, so nothing that already catches one has to change.
 */
export class ResponseError extends Error {
  constructor(
    message: string,
    readonly appErrorCode?: string,
  ) {
    super(message);
    this.name = 'ResponseError';
  }
}

interface ErrorResponseBody {
  message?: string;
  appErrorCode?: string;
}

/**
 * The error thrown for a response that carries a body: its message if it has one, and its
 * code either way. A rejection with no response at all is the request never leaving, so
 * there the local message is all there is.
 */
const responseError = (error: unknown) => {
  if (error !== null && typeof error === 'object' && 'response' in error) {
    const { message, appErrorCode } = (error as AxiosError<ErrorResponseBody>).response?.data ?? {};
    return new ResponseError(message ?? 'Unknown response error.', appErrorCode);
  }

  return new ResponseError(error instanceof Error ? error.message : 'Unknown request error.');
};

const publicApis = new Set([
  '/business/abn-check',
  '/business/acn-check',
  '/users/check',
  '/users/complete-signup',
  '/users/signup/business',
  '/users/signup/individual',
  '/validate/reset-password',
  '/validate/send-confirmation-email-with-code',
  '/validate/send-join-email',
  '/validate/send-reset-password-email',
]);

const globalUrls = new Set(['/projects/list', '/ras/actions', '/app/config', '/products']);

const invitedSignUpApi = /^\/users\/complete-signup\/[\dA-Za-z\-]/;
const userConsentApi = /^\/users\/consent\/\w/;

/**
 * Check if the API is public.
 *
 * @param url - API url
 * @returns True if the API is public
 */
const isPublicApi = (url?: string) => {
  if (!url) return true;
  return (
    publicApis.has(url) ||
    invitedSignUpApi.test(url) ||
    userConsentApi.test(url) ||
    (url.startsWith('/auth') && url !== '/auth/signout')
  );
};

/**
 * Check if the url is public that only use userGroupId from user manager.
 *
 * @param url - API url
 * @returns True if the API is public
 */
const isGlobalUrl = (url?: string) => {
  if (!url) return true;
  return (
    globalUrls.has(url) ||
    url.startsWith('/users/') ||
    url.startsWith('/user-groups/') ||
    url.startsWith('/transaction-account/') ||
    url.includes('/features/subscribe') ||
    url.includes('/features/unsubscribe')
  );
};

const isPayClaimApi = (url?: string) => {
  if (!url) return false;
  return ['/claims/pay', '/claims/one-time-code'].includes(url);
};

const timeout = 29_000;
const maxAge = 10_000;

export const NOT_FOUND_ERROR_MESSAGE = 'Not found.';
export const USER_NOT_FOUND_ERROR_MESSAGE = 'User not found.';
export const USER_INACTIVE_ERROR_MESSAGE = 'User is not active.';
export const INTERNAL_SERVER_ERROR_MESSAGE = 'Internal server error.';

export const request = axios.create({
  baseURL: apiBaseUrl,
  timeout,
  timeoutErrorMessage: `Request timeout in ${Math.round(timeout / 1000)} seconds.`,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Refresh access token function.
 *
 * @returns New access token.
 */
export async function refreshToken() {
  try {
    const access_token = globalThis.sessionStorage.getItem('access_token');
    const { data: res } = await request.post<RefreshTokenResponse>('/auth/refresh-token', {
      access_token,
    });
    const accessToken = res.data.access_token;
    if (!accessToken) return;
    globalThis.sessionStorage.setItem('access_token', accessToken);
    return accessToken;
  } catch {
    globalThis.sessionStorage.removeItem('access_token');
    const searchParams = new URLSearchParams(globalThis.location.search);
    searchParams.set('next', globalThis.location.pathname);
    searchParams.sort();
    await Router.replace(`/signin?${searchParams.toString()}`);
  }
}

export const memoizedRefreshToken = memoize(refreshToken, {
  cache: new ExpiryMap(maxAge),
});

/**
 * Recursively replaces non-ASCII hyphens with ASCII hyphen in string values.
 * Preserves the shape of the data (object keys, array order, other types unchanged).
 *
 * @param data - Request payload (object or array)
 * @returns New structure with same shape and hyphen-normalized strings
 */
export function fixNonASCIIChars<T>(data: T): T {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data === 'string') {
    return data
      .replaceAll(NULL_BYTE_REGEX, '')
      .replaceAll(NON_ASCII_HYPHEN_REGEX, ASCII_HYPHEN) as T;
  }

  if (Array.isArray(data)) {
    return data.map((item) => fixNonASCIIChars(item)) as T;
  }

  if (typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, fixNonASCIIChars(value)]),
    ) as T;
  }

  return data;
}

export const findNonASCIIChar = (
  object: Record<string, unknown> | unknown[],
): [key: string, value: string] | null => {
  const entries = Object.entries(object);

  for (const [key, value] of entries) {
    if (!value) {
      continue;
    }

    if (Array.isArray(value)) {
      const result = findNonASCIIChar(value);
      if (result) return [key, result[1]];
      continue;
    }

    if (typeof value === 'object') {
      const result = findNonASCIIChar(value as Record<string, unknown>);
      if (result) return result;
      continue;
    }

    if (typeof value === 'string' && !asciiPattern.test(value)) {
      return [key, value];
    }
  }

  return null;
};

export const newAbortSignal = (timeoutMs: number, reason?: string) => {
  const abortController = new AbortController();
  setTimeout(() => abortController.abort(reason), timeoutMs || 0);
  return abortController.signal;
};

request.interceptors.request.use(
  (config) => {
    if (!config.url) {
      const abortReason = 'Request url is required';
      config.signal = newAbortSignal(0, abortReason);
      return config;
    }

    const requestId = uuidv4();

    if (
      !isEmpty(config.data) &&
      config.method &&
      ['post', 'put', 'patch'].includes(config.method.toLowerCase())
    ) {
      // Send the cleaned payload, not the original: the strip is the point, not just the check.
      config.data = fixNonASCIIChars(config.data);
      const entry = findNonASCIIChar(config.data);

      if (entry) {
        const [key, value] = entry;
        const abortReason = `Non-ASCII characters "${value}" found in "${key}" field, please remove and try again.`;
        reportAppError({
          type: 'VALIDATION',
          code: 'NON_ASCII_PAYLOAD',
          requestId,
          requestUrl: (config.baseURL ?? '') + (config.url ?? ''),
          details: {
            non_ascii_key: key,
            non_ascii_value: value,
          },
        });
        throw new Error(abortReason);
      }
    }

    config.headers['X-RequestId'] = requestId;

    if (isPublicApi(config.url)) {
      return config;
    }

    const accessToken = globalThis.sessionStorage.getItem('access_token');

    if (!accessToken) {
      const abortReason = 'Access token is required for protected APIs';
      config.signal = newAbortSignal(200, abortReason);
      console.error(abortReason);
      return config;
    }

    config.headers.Authorization = `Bearer ${accessToken}`;

    const { accounts, userGroupId } = userManager;
    if (isPayClaimApi(config.url)) {
      const { currentParty } = contractManager;
      const partyAccountId = currentParty?.account?.id;
      if (partyAccountId) {
        const userGroupId = accounts.find((account) => account.id === partyAccountId)?.userGroup.id;
        if (userGroupId) {
          config.headers['X-UserGroup'] = userGroupId;
          return config;
        }
      }
    }

    const { userGroup } = projectManager;
    const groupId = userGroup?.id || userGroupId;
    if (userGroupId && isGlobalUrl(config.url)) {
      config.headers['X-UserGroup'] = userGroupId;
    } else if (groupId) {
      config.headers['X-UserGroup'] = groupId;
    }
    return config;
  },
  (error) => Promise.reject(error instanceof Error ? error : new Error('Unknown request error.')),
);

request.interceptors.response.use(
  (response) => response,
  async (error) => {
    switch (error.response?.status) {
      case 500:
      case 501:
      case 502:
      case 503:
      case 504: {
        throw new Error(INTERNAL_SERVER_ERROR_MESSAGE);
      }

      case 401: {
        const lastRequestConfig = error.config as AxiosRequestConfig & { sent: boolean };

        if (isPublicApi(lastRequestConfig.url)) {
          throw responseError(error);
        }

        if (!lastRequestConfig.sent) {
          lastRequestConfig.sent = true;
          const accessToken = await memoizedRefreshToken();

          if (!accessToken) {
            globalThis.sessionStorage.removeItem('access_token');
            throw new Error('Refresh token failed, please sign in again.');
          }

          lastRequestConfig.headers = {
            ...lastRequestConfig.headers,
            Authorization: `Bearer ${accessToken}`,
          };

          return request(lastRequestConfig);
        }

        globalThis.sessionStorage.removeItem('access_token');
        throw new Error('Refresh token failed, please sign in again.');
      }

      case 403: {
        throw new Error('Forbidden.');
      }

      case 404: {
        switch (error.response.config.url) {
          case '/users/search/name-by-email': {
            throw new Error(USER_NOT_FOUND_ERROR_MESSAGE);
          }
          default: {
            throw new Error(NOT_FOUND_ERROR_MESSAGE);
          }
        }
      }

      case 409: {
        if (error.response.config.url.includes('challenge/address')) {
          throw error.response?.data;
        }
        if (
          error.response.config.url.includes('batch-claim-payment-requests') &&
          (error.response.config.url.includes('draft') ||
            error.response.config.url.includes('edit'))
        ) {
          throw error.response?.data;
        }
        throw responseError(error);
      }

      case 422: {
        const retryAllowed: boolean = error.response?.data?.retryAllowed ?? false;
        if (retryAllowed) {
          await new Promise((resolve) => setTimeout(resolve, 5000));
          const lastRequestConfig = error.config as AxiosRequestConfig;
          return request(lastRequestConfig);
        }
        throw responseError(error);
      }

      case 423: {
        if (error.response.config.url === '/users/search/name-by-email') {
          throw new Error(USER_INACTIVE_ERROR_MESSAGE);
        }
        throw responseError(error);
      }

      default: {
        throw responseError(error);
      }
    }
  },
);
